/**
 * Pure status-chip state machine for the Execution Schedule view.
 *
 * Given a ScheduleRow + context (today/past/future + current IST time),
 * returns the chip variant + label for the entry slot and the exit slot.
 *
 * All domain strings come from tradingConstants — no hardcoded literals.
 */

import {
  ENTRY_STATUS,
  EXIT_STATUS,
  POSITION_STATUS,
} from '../constants/tradingConstants';
import type { ChipState, ScheduleRow, ScheduleStrategySummary } from '../types/schedule';

/** Grace window (minutes) after a scheduled trigger before we declare "Missed". */
export const SCHEDULE_GRACE_MINUTES = 2;

export interface ChipContext {
  isToday: boolean;
  isPast: boolean;
  /** Current IST time as "HH:MM:SS" — required when isToday, ignored otherwise. */
  nowIstHms: string | null;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function hhmmToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

function hmsToMinutes(hms: string): number {
  const [h, m] = hms.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

/** Format an ISO timestamp as "HH:MM:SS" in IST for compact chip labels. */
function formatTimeIst(iso: string | null): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('en-GB', {
      timeZone: 'Asia/Kolkata',
      hour12: false,
    });
  } catch {
    return '';
  }
}

function formatPrice(value: number | null): string {
  return value != null ? value.toFixed(2) : '';
}

// ── Entry chip ─────────────────────────────────────────────────────────────

/**
 * Entry chip for a row. Returns null when the row has no scheduled entry
 * on this date (e.g. BTST exit-only row on the day after entry).
 */
export function computeEntryChip(row: ScheduleRow, ctx: ChipContext): ChipState | null {
  if (!row.scheduled_entry_time) return null;

  const scheduledLabel = row.scheduled_entry_time;

  // Overlay wins — live status reflects what actually happened.
  if (row.entry_status === ENTRY_STATUS.COMPLETE) {
    const t = formatTimeIst(row.entry_filled_at);
    const price = formatPrice(row.average_entry_price);
    const parts = ['Filled', t, price && `@ ${price}`].filter(Boolean);
    return { variant: 'green', label: parts.join(' ') };
  }

  if (row.entry_status === ENTRY_STATUS.REJECTED) {
    return {
      variant: 'red',
      label: 'Rejected',
      subtext: row.entry_error_message || undefined,
    };
  }

  if (row.entry_status === ENTRY_STATUS.CANCELLED) {
    return { variant: 'gray', label: 'Cancelled' };
  }

  if (
    row.entry_status === ENTRY_STATUS.TRIGGER_PENDING
    || row.entry_status === ENTRY_STATUS.OPEN
    // PENDING_PLACE (Phase 6, claim-then-place in flight ~1-3s at backend —
    // will backfill to TRIGGER_PENDING / COMPLETE / REJECTED). Without this
    // branch a row polled during a delayed-EventBridge stuck-claim window
    // would fall through to the clock-based fallback and misrender as red
    // 'Missed' on today past the grace window.
    || row.entry_status === ENTRY_STATUS.PENDING_PLACE
    // UL_MONITOR_PENDING (Phase 5b) — UL-W&T invisible-wait, no broker
    // order rests; monitor is polling spot. From the user's perspective
    // this reads the same as TRIGGER_PENDING (armed, awaiting condition).
    // Mirror of backend _tally_entry_status which folds this into the
    // same pending bucket.
    || row.entry_status === ENTRY_STATUS.UL_MONITOR_PENDING
  ) {
    return { variant: 'yellow', label: 'Pending' };
  }

  // No overlay — decide from date + clock.
  if (ctx.isPast) {
    return { variant: 'red', label: 'Missed', subtext: 'strategy did not run' };
  }

  if (ctx.isToday && ctx.nowIstHms) {
    const now = hmsToMinutes(ctx.nowIstHms);
    const scheduled = hhmmToMinutes(row.scheduled_entry_time);
    if (now < scheduled) {
      return { variant: 'yellow', label: `Scheduled ${scheduledLabel}` };
    }
    if (now <= scheduled + SCHEDULE_GRACE_MINUTES) {
      return { variant: 'yellow', label: 'Triggering…' };
    }
    return { variant: 'red', label: 'Missed', subtext: 'no order placed' };
  }

  // Future day — no position yet, not anchored to any clock.
  return { variant: 'gray', label: scheduledLabel };
}

// ── Exit chip ──────────────────────────────────────────────────────────────

/**
 * Exit chip for a row. Returns null when the row has no scheduled exit
 * on this date.
 */
export function computeExitChip(row: ScheduleRow, ctx: ChipContext): ChipState | null {
  if (!row.scheduled_exit_time) return null;

  const scheduledLabel = row.scheduled_exit_time;

  // Terminal: position closed — show how it closed.
  if (row.position_status === POSITION_STATUS.CLOSED) {
    const t = formatTimeIst(row.exit_placed_at);
    const price = formatPrice(row.exit_average_price);
    const suffix = price ? `@ ${price}` : '';
    if (row.exit_type === 'SL') {
      return { variant: 'green', label: ['SL hit', suffix].filter(Boolean).join(' ') };
    }
    if (row.exit_type === 'TP') {
      return { variant: 'green', label: ['Target hit', suffix].filter(Boolean).join(' ') };
    }
    if (row.exit_type === 'EOD') {
      return { variant: 'green', label: 'Closed @ EOD' };
    }
    if (row.exit_type === 'EXPIRY_SETTLED') {
      return { variant: 'green', label: 'Settled at expiry' };
    }
    const parts = ['Exited', t, suffix].filter(Boolean);
    return { variant: 'green', label: parts.join(' ') };
  }

  // Exit order placed/failed but position not yet CLOSED.
  if (row.exit_status === EXIT_STATUS.COMPLETE) {
    const price = formatPrice(row.exit_average_price);
    return { variant: 'green', label: price ? `Exited @ ${price}` : 'Exited' };
  }
  if (row.exit_status === EXIT_STATUS.REJECTED) {
    return {
      variant: 'red',
      label: 'Exit failed',
      subtext: row.exit_error_message || undefined,
    };
  }
  if (row.exit_status === EXIT_STATUS.CANCELLED) {
    return { variant: 'gray', label: 'Exit cancelled' };
  }
  if (row.exit_status === EXIT_STATUS.OPEN) {
    return { variant: 'yellow', label: 'Exiting…' };
  }

  // Position is OPEN but no exit activity yet.
  if (row.position_status === POSITION_STATUS.OPEN) {
    if (ctx.isToday && ctx.nowIstHms) {
      const now = hmsToMinutes(ctx.nowIstHms);
      const scheduled = hhmmToMinutes(row.scheduled_exit_time);
      if (now < scheduled) {
        return { variant: 'yellow', label: `Scheduled ${scheduledLabel}` };
      }
      if (now <= scheduled + SCHEDULE_GRACE_MINUTES) {
        return { variant: 'yellow', label: 'Exiting…' };
      }
      return { variant: 'red', label: 'Missed exit' };
    }
    // Future-day exit for a position already OPEN (BTST looking ahead).
    return { variant: 'gray', label: scheduledLabel };
  }

  // No overlay yet — synthesized.
  if (ctx.isPast) {
    // No record of exit on a past day — can't know what happened without overlay.
    return { variant: 'gray', label: scheduledLabel };
  }
  if (ctx.isToday && ctx.nowIstHms) {
    const now = hmsToMinutes(ctx.nowIstHms);
    const scheduled = hhmmToMinutes(row.scheduled_exit_time);
    if (now < scheduled) {
      return { variant: 'gray', label: scheduledLabel };
    }
    return { variant: 'gray', label: scheduledLabel };
  }
  return { variant: 'gray', label: scheduledLabel };
}
// ── Strategy-level chips (Phase B) ─────────────────────────────────────────
//
// Read aggregated status counts from ScheduleStrategySummary instead of
// iterating per-leg ScheduleRow objects. The counts are server-computed;
// the only clock-sensitive logic remains here (grace window, future-day
// tinting) because it depends on the render-time IST clock.

/**
 * Entry chip for a strategy row on the main Schedule table.
 * Returns null when no entry is scheduled on this date (e.g. BTST exit-only).
 *
 * Denominator semantics: the backend only counts legs with real activity on
 * past days (omits `none`), so this chip reads "Filled 1/1" when only one
 * of two legs ran — not the misleading "1/2 filled · 1 pending". On today
 * and future days every leg is counted so activeTotal equals leg_count.
 */
export function computeStrategyEntryChip(
  s: ScheduleStrategySummary,
  ctx: ChipContext,
): ChipState | null {
  if (!s.scheduled_entry_time) return null;
  const e = s.entry_counts;
  const inFlight = e.pending + e.open;  // TRIGGER_PENDING + OPEN
  const activeTotal = e.complete + e.rejected + e.cancelled + inFlight;
  const total = activeTotal > 0 ? activeTotal : s.leg_count;

  // Broker rejection → red chip, worst-wins. Cancelled entries are user- or
  // system-initiated and do NOT turn the chip red; they surface separately below.
  const failed = e.rejected;
  if (failed > 0) {
    return {
      variant: 'red',
      label: `${failed}/${total} failed`,
      subtext: s.entry_error || undefined,
    };
  }

  // Fully filled → green.
  if (total > 0 && e.complete === total) {
    return { variant: 'green', label: `Filled ${e.complete}/${total}` };
  }

  // Partial fill → yellow with progress. Cancelled legs stay in the denominator
  // so the ratio stays honest ("1/3 filled" when 2 were cancelled).
  if (e.complete > 0) {
    return { variant: 'yellow', label: `${e.complete}/${total} filled` };
  }

  // Any broker-side in-flight (TRIGGER_PENDING/OPEN) → yellow "Pending".
  if (inFlight > 0) {
    return { variant: 'yellow', label: 'Pending' };
  }

  // Only cancellations, nothing filled / pending / rejected → gray informational
  // chip. Prevents the clock-based fallback from mislabelling this as "Missed".
  if (e.cancelled > 0) {
    return { variant: 'gray', label: `${e.cancelled} cancelled` };
  }

  // All synthesized (no position records yet) — decide by clock.
  if (ctx.isPast) {
    return { variant: 'red', label: 'Missed', subtext: 'strategy did not run' };
  }
  if (ctx.isToday && ctx.nowIstHms) {
    const now = hmsToMinutes(ctx.nowIstHms);
    const scheduled = hhmmToMinutes(s.scheduled_entry_time);
    if (now < scheduled) {
      return { variant: 'yellow', label: `Scheduled ${s.scheduled_entry_time}` };
    }
    if (now <= scheduled + SCHEDULE_GRACE_MINUTES) {
      return { variant: 'yellow', label: 'Triggering…' };
    }
    return { variant: 'red', label: 'Missed', subtext: 'no orders placed' };
  }
  return { variant: 'gray', label: s.scheduled_entry_time };
}

/**
 * Exit chip for a strategy row.
 * Returns null when no exit is scheduled on this date.
 */
export function computeStrategyExitChip(
  s: ScheduleStrategySummary,
  ctx: ChipContext,
): ChipState | null {
  if (!s.scheduled_exit_time) return null;
  const x = s.exit_counts;
  // Denominate on the exit-slot position counts.
  //
  // Why not open_count/closed_count: on a BTST entry day those counters
  // include today's fresh entries that are destined to exit NEXT trading
  // day — not today. Counting them here used to produce phantom
  // "Missed exit" chips when today's new entries sat open past the
  // scheduled exit time. exit_slot_* excludes carry.
  //
  // Why not exit_counts: positions closed via SL have a blank `exit_status`
  // because the SL order itself closes them. `exit_counts.complete` only
  // tracks explicit EXIT/EOD/TP orders and silently under-counts SL
  // closures. exit_slot_closed_count is position-level and catches them.
  const totalPositions = s.exit_slot_closed_count + s.exit_slot_open_count;
  const totalForFailed = totalPositions > 0 ? totalPositions : s.leg_count;

  // Exit slot empty — no positions in the slot yet.
  //
  // Two distinct cases hide behind a zero denominator and must be
  // coloured differently:
  //
  //   exitIsForToday (SAME_DAY / POSITIONAL / BTST exit-day view):
  //     the scheduled exit WILL fire today. Positions may still enter
  //     (e.g. WAT waiting for trigger, ORB pre-breakout) before the
  //     scheduled time. Render as a yellow "Scheduled HH:MM" chip so
  //     the user can see activity is expected today — same affordance
  //     as the clock branch below.
  //
  //   !exitIsForToday (BTST entry-day view):
  //     the scheduled exit is for yesterday's positions, and yesterday
  //     had none. Today's fresh entries (carry) will exit tomorrow, not
  //     today. Render gray so the chip doesn't falsely advertise
  //     today's action.
  //
  // `linked_entry_date === null` (POSITIONAL) is treated as
  // exit-for-today because POSITIONAL always exits today per DTE rule.
  if (totalPositions === 0) {
    const carry = s.carry_open_count;
    const exitIsForToday =
      s.linked_entry_date == null
      || s.linked_entry_date === s.scheduled_exit_date;

    if (!exitIsForToday) {
      return {
        variant: 'gray',
        label: `Scheduled ${s.scheduled_exit_time}`,
        subtext: carry > 0 ? `${carry} new` : undefined,
      };
    }

    // Exit is for today. Apply clock-style rendering — yellow pre-time,
    // neutral gray past grace (no positions to miss, so not red).
    if (ctx.isToday && ctx.nowIstHms) {
      const now = hmsToMinutes(ctx.nowIstHms);
      const scheduled = hhmmToMinutes(s.scheduled_exit_time);
      if (now < scheduled) {
        return { variant: 'yellow', label: `Scheduled ${s.scheduled_exit_time}` };
      }
      if (now <= scheduled + SCHEDULE_GRACE_MINUTES) {
        return { variant: 'yellow', label: 'Exiting…' };
      }
    }
    return { variant: 'gray', label: `Scheduled ${s.scheduled_exit_time}` };
  }

  // Terminal: every exit-slot position is closed (any path — SL / EXIT / EOD / TP).
  if (s.exit_slot_closed_count === totalPositions) {
    return { variant: 'green', label: `Closed ${s.exit_slot_closed_count}/${totalPositions}` };
  }

  // Broker rejection of an exit order → red. Cancelled exits are not errors
  // and surface separately below as a gray informational chip.
  const failed = x.rejected;
  if (failed > 0) {
    return {
      variant: 'red',
      label: `${failed}/${totalForFailed} failed`,
      subtext: s.exit_error || undefined,
    };
  }

  // Exit orders in flight (OPEN; exit has no TRIGGER_PENDING).
  if (x.pending + x.open > 0) {
    return { variant: 'yellow', label: 'Exiting…' };
  }

  // Only cancellations with no closures / failures / in-flight → gray.
  if (x.cancelled > 0 && s.exit_slot_closed_count === 0) {
    return { variant: 'gray', label: `${x.cancelled} cancelled` };
  }

  // Partial close — some exit-slot positions closed, others still live.
  if (s.exit_slot_closed_count > 0) {
    return {
      variant: 'yellow',
      label: `${s.exit_slot_closed_count}/${totalPositions} closed`,
    };
  }

  // Nothing closed yet, exit-slot positions still OPEN → clock-based labels.
  if (s.exit_slot_open_count > 0 && ctx.isToday && ctx.nowIstHms) {
    const now = hmsToMinutes(ctx.nowIstHms);
    const scheduled = hhmmToMinutes(s.scheduled_exit_time);
    if (now < scheduled) {
      return { variant: 'yellow', label: `Scheduled ${s.scheduled_exit_time}` };
    }
    if (now <= scheduled + SCHEDULE_GRACE_MINUTES) {
      return { variant: 'yellow', label: 'Exiting…' };
    }
    return { variant: 'red', label: 'Missed exit' };
  }

  // Synthesized / future / past without position overlay.
  return { variant: 'gray', label: s.scheduled_exit_time };
}

/**
 * SL health chip for a strategy. Returns null when there are zero live
 * positions with SL relevance (all `not_needed`) — the column is left
 * blank instead of rendering a sea of dashes.
 *
 * Rules:
 *   any rejected    → red     "N/M SL failed" + first error as subtext
 *   any pending     → yellow  "N SL PENDING" (morning re-placement needed)
 *   placed only     → green   "SL N/N"
 *   all not_needed  → null    (chip hidden)
 */
export function computeStrategySlChip(
  s: ScheduleStrategySummary,
): ChipState | null {
  const c = s.sl_counts;
  const live = c.placed + c.rejected + c.pending;
  if (live === 0) return null;

  if (c.rejected > 0) {
    return {
      variant: 'red',
      label: `${c.rejected}/${live} SL failed`,
      subtext: s.sl_error || undefined,
    };
  }
  if (c.pending > 0) {
    // PENDING uppercased so the actionable state stands out next to the
    // healthy "SL N/N" label.
    return {
      variant: 'yellow',
      label: `${c.pending} SL PENDING`,
      subtext: 'morning re-placement expected',
    };
  }
  // placed === live
  return { variant: 'green', label: `SL ${c.placed}/${live}` };
}

/**
 * Single overall-status label that collapses entry / SL / exit signals into one
 * at-a-glance chip. Used by the Schedule tables so the trader can scan the row
 * without parsing three separate chips.
 *
 * Priority (first match wins):
 *   1. Any rejection or stuck OPEN order         → ERROR
 *   2. All legs closed                           → CLOSED
 *   3. Anything awaiting (TRIGGER_PENDING)       → PENDING
 *   4. At least one leg open and running cleanly → ACTIVE
 *   5. No positions yet (future day, etc.)       → SCHEDULED
 */
export type StrategyOverallStatus = 'ERROR' | 'CLOSED' | 'PENDING' | 'ACTIVE' | 'SCHEDULED';

export function computeStrategyOverallStatus(
  s: ScheduleStrategySummary,
): StrategyOverallStatus {
  const e = s.entry_counts;
  const x = s.exit_counts;
  const sl = s.sl_counts;

  if (
    e.rejected > 0 || x.rejected > 0 || sl.rejected > 0
    || e.open > 0 || x.open > 0 || sl.open > 0
  ) {
    return 'ERROR';
  }
  if (s.leg_count > 0 && s.closed_count === s.leg_count) return 'CLOSED';
  if (e.pending > 0 || x.pending > 0 || sl.pending > 0) return 'PENDING';
  if (s.open_count > 0) return 'ACTIVE';
  return 'SCHEDULED';
}

// ── State column (single-chip collapsed verdict) ───────────────────────────
//
// 4-state model used by the new `State` column on the Schedule table:
//   RUNNING   = the strategy is live OR awaiting a trigger
//   CLOSED    = the strategy ran and is now done (no further activity)
//   ERROR    = a broker rejection or stuck OPEN order needs attention
//   SCHEDULED = nothing has happened yet (future day / pre-entry today)
//
// Priority is ERROR → RUNNING → CLOSED → SCHEDULED. Critically, RUNNING is
// checked before CLOSED so that a strategy whose parent positions have
// closed but whose re-entry / re-execute children are armed (TRIGGER_PENDING)
// still reads as RUNNING — re-entry chains are first-class motion.
//
// Re-entry flavours covered (all aggregated into `entry_counts.pending`):
//   • SL_REENTRY  — two-phase candle gate after SL fill
//   • SL_RECOST   — immediate re-place at root_expected_entry_price
//   • SL_REEXEC   — fresh strike pick after SL fill
//   • TP_REEXEC   — fresh strike pick after TP fill

export type StrategyRunState = 'RUNNING' | 'CLOSED' | 'ERROR' | 'SCHEDULED';

export interface StrategyRunStateChip {
  state: StrategyRunState;
  variant: 'green' | 'red' | 'gray';
  /** Short label rendered inside the chip — e.g. "RUNNING 2/3", "ERROR 1/3",
   *  "AWAITING" (positions all closed but re-entry / WAT / ORB armed). */
  label: string;
  /** Optional broker error message OR descriptive context — surfaces as the
   *  chip's title tooltip. */
  tooltip?: string;
}

/**
 * Collapse a strategy's entry / SL / exit / position counts into a single
 * State chip for the Schedule table.
 *
 * Label rules:
 *   ERROR     — "ERROR N/M" (N=rejected-or-stuck, M=legs touched), or "Missed"
 *               for a past-day scheduled run that left no position record.
 *   RUNNING   — "RUNNING N/M" when open_count > 0 (N=open, M=leg_count).
 *               "AWAITING"   when open_count = 0 but any pending exists
 *                            (covers WAT, ORB, SL_REENTRY, SL_RECOST,
 *                             SL_REEXEC, TP_REEXEC uniformly — tooltip
 *                             names which counts are non-zero).
 *   CLOSED    — "CLOSED N/M" when closed_count ≤ leg_count.
 *               "CLOSED"     when closed_count > leg_count (chain exhausted —
 *                            ratio would mislead).
 *               "Cancelled"  when only cancellations and no closures.
 *   SCHEDULED — "Scheduled HH:MM" or "Scheduled".
 */
export function computeStrategyRunState(
  s: ScheduleStrategySummary,
  ctx: ChipContext,
): StrategyRunStateChip {
  const e = s.entry_counts;
  const x = s.exit_counts;
  const sl = s.sl_counts;

  // ── 1. ERROR ──────────────────────────────────────────────────────────────
  // Broker rejections OR stuck OPEN orders OR (today only) missed scheduled
  // exits win every other state. Missed-exit is only emitted by the backend
  // for today's view (decision B); the `!ctx.isPast` belt-and-suspenders
  // ensures past-day responses can't accidentally upgrade the State pill.
  const rejected = e.rejected + x.rejected + sl.rejected;
  const stuckOpen = e.open + x.open + sl.open;
  const missedExitCount = s.missed_exit_count ?? 0;
  const hasMissedExitToday = !ctx.isPast && missedExitCount > 0;
  if (rejected > 0 || stuckOpen > 0 || hasMissedExitToday) {
    // Denominator: legs touched by any activity so far (or leg_count if none).
    const activeEntries =
      e.complete + e.rejected + e.cancelled + e.pending + e.open;
    const denom = activeEntries > 0 ? activeEntries : s.leg_count;
    // Headline numerator priority: broker rejection > stuck OPEN > missed-exit.
    // Rejections / stuck are broker-side and demand operator action; missed-exit
    // is platform-side and is also surfaced via the dedicated Health column box
    // — keeping rejection primary preserves existing operator muscle memory.
    const bad = rejected > 0 ? rejected : (stuckOpen > 0 ? stuckOpen : missedExitCount);
    const label = denom > 0 ? `ERROR ${bad}/${denom}` : 'ERROR';
    // Tooltip composition (decision C): rejection error first, then append
    // "· N exit(s) missed" when both rejected AND missed-exit conditions
    // co-occur. Missed-exit-only state gets the missed-exit message alone.
    const errMsg = s.entry_error || s.sl_error || s.exit_error || undefined;
    let tooltip = errMsg;
    if (hasMissedExitToday) {
      const missedSuffix = `${missedExitCount} ${missedExitCount === 1 ? 'exit' : 'exits'} missed`;
      tooltip = tooltip ? `${tooltip} · ${missedSuffix}` : missedSuffix;
    }
    return {
      state: 'ERROR',
      variant: 'red',
      label,
      tooltip,
    };
  }

  // Past-day scheduled run with no positions recorded — surfaces as ERROR
  // ("Missed") so history views stay loud. Has no broker error to attach.
  if (ctx.isPast && s.scheduled_entry_time && !s.has_positions) {
    return {
      state: 'ERROR',
      variant: 'red',
      label: 'Missed',
      tooltip: 'no positions recorded for this scheduled run',
    };
  }

  // ── 2. RUNNING ────────────────────────────────────────────────────────────
  // Anything in motion — must precede CLOSED so re-entry pending wins over
  // "parent closed". `entry_counts.pending` includes WAT/ORB armed entries
  // AND SL/TP re-entry children awaiting trigger.
  const armedPending = e.pending + x.pending + sl.pending;
  if (s.open_count > 0) {
    return {
      state: 'RUNNING',
      variant: 'green',
      label: s.leg_count > 0
        ? `RUNNING ${s.open_count}/${s.leg_count}`
        : `RUNNING ${s.open_count}`,
    };
  }
  if (armedPending > 0) {
    // No live legs but something is armed — could be WAT, ORB, SL_REENTRY /
    // SL_RECOST / SL_REEXEC / TP_REEXEC. Generic "AWAITING" so the chip
    // doesn't have to know which path. Tooltip names the axes that are armed.
    const sources: string[] = [];
    if (e.pending > 0) sources.push(`entry × ${e.pending}`);
    if (x.pending > 0) sources.push(`exit × ${x.pending}`);
    if (sl.pending > 0) sources.push(`SL × ${sl.pending}`);
    // If any legs already closed, surface that — most often this is the
    // "SL fill, re-entry armed" case; the trader's first question is "is
    // re-entry coming?" and the tooltip needs to confirm it.
    const closedHint = s.closed_count > 0 ? ` after ${s.closed_count} closed` : '';
    return {
      state: 'RUNNING',
      variant: 'green',
      label: 'AWAITING',
      tooltip: `Awaiting trigger: ${sources.join(', ')}${closedHint}`,
    };
  }

  // ── 3. CLOSED ─────────────────────────────────────────────────────────────
  // Strategy ran and is now terminal. Reached here means no rejections, no
  // stuck orders, no live positions, no pending activity.
  if (s.closed_count > 0) {
    // Chain-aware label: if more positions closed than the strategy has legs
    // (re-entry / TP_REEXEC chain ran), the N/M ratio would mislead. Surface
    // the raw chain count in the tooltip instead.
    if (s.leg_count > 0 && s.closed_count <= s.leg_count) {
      return {
        state: 'CLOSED',
        variant: 'gray',
        label: `CLOSED ${s.closed_count}/${s.leg_count}`,
      };
    }
    return {
      state: 'CLOSED',
      variant: 'gray',
      label: 'CLOSED',
      tooltip: s.leg_count > 0
        ? `${s.closed_count} positions closed across ${s.leg_count} legs (chain)`
        : `${s.closed_count} positions closed`,
    };
  }

  // Cancelled-only row — entries cancelled before any fill. No closures, no
  // errors, no pending. Surfaces as CLOSED-grey with an explicit "Cancelled"
  // label so it doesn't read as SCHEDULED.
  if (e.cancelled > 0 || x.cancelled > 0 || (sl.cancelled ?? 0) > 0) {
    return {
      state: 'CLOSED',
      variant: 'gray',
      label: 'Cancelled',
    };
  }

  // ── 4. SCHEDULED ──────────────────────────────────────────────────────────
  // No positions yet. Show scheduled entry time when known.
  return {
    state: 'SCHEDULED',
    variant: 'gray',
    label: s.scheduled_entry_time ? `Scheduled ${s.scheduled_entry_time}` : 'Scheduled',
  };
}
